import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider
} from '@mui/material';

import WarningIcon from '@mui/icons-material/Warning';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import Breadcrumbs from 'config/Breadcrumb';

import { fetchAlertById as fetchAlertByIdService } from 'services/modules/vapt/alert';

import he from 'he';
import parse from 'html-react-parser';

function SingleAlertPage() {
  // useParams hook
  const { id } = useParams();

  // Redux dispatch
  const dispatch = useDispatch();

  // State variables
  const [showRemediationButton, setShowRemediationButton] = useState(true);
  const [showEvidenceButton, setShowEvidenceButton] = useState(true);
  const [mainDimensionLabel, setMainDimensionLabel] = useState('');
  const [threatActor, setThreatActor] = useState(false);
  const [currentPath, setCurrentPath] = useState(['']);

  // Redux selectors
  const dimensionsContainer = useSelector((state) => state.vaptData.dimensions);
  const threatContainer = useSelector((state) => state.vaptAlert.threat);
  const vaptDimensionsContainer = useSelector((state)=>state.vaptData.vaptDimensions);


  // useEffect hook fetches alert by id and dimensions
  useEffect(() => {
    if (id !== null) {
      dispatch(fetchAlertByIdService({ id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect hook sets the main dimension label
  useEffect(() => {
    if (threatContainer && threatContainer.dimension && dimensionsContainer) {
      const mainDimension = dimensionsContainer
        .filter((dim) => !!dim.subDimensions)
        .find((dim) => dim.subDimensions.find((sub) => sub.id === threatContainer.dimension.id));

      if (mainDimension) {
        setMainDimensionLabel(mainDimension.label);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionsContainer, threatContainer]);
  
  useEffect(() => {
    if(threatContainer?.report && vaptDimensionsContainer?.length>0 && mainDimensionLabel){
        const category = vaptDimensionsContainer?.find((cat)=> cat?._id === threatContainer.report.category);
        setCurrentPath([category.label, threatContainer.report.subcategory, threatContainer.dimension.label, mainDimensionLabel]);
    }
  },[threatContainer, vaptDimensionsContainer, mainDimensionLabel]);

  // Formats the property names
  function formatPropertyName(property) {
    if (property === 'cve') {
      return property.toUpperCase();
    } else {
      return property.replace(/_/g, ' ').replace(/(?:^|\s)\S/g, function (a) {
        return a.toUpperCase();
      });
    }
  }

  // This method opens threat actor dialog in case if any threat actors are present
  const handleThreatActorClick = () => {
    if (threatContainer.data.ThreatActor !== 'Not Applicable') {
      setThreatActor(true);
    }
  };
  
  // Decodes the html from url encoded string for displaying the evidence
  const decodeHtmlEntities = (htmlString) => {
    const decodedString = he.decode(htmlString);
    const style = 'style="max-width: 100%; height: auto;"';
    return decodedString.replace(/<img /g, `<img ${style} `);
  };

  return (
    <>
      <div className='m-2'>
        {!!!mainDimensionLabel ? (
          <div className="loading-spinner-container ">
            <CircularProgress />
          </div>
        ) : (
          <Card>
            <CardHeader
              title={
                <React.Fragment>

                  <div className="card-head mt-2">
                    <div className="flex-head">
                      <label className="label">
                        <Breadcrumbs paths={currentPath} />
                      </label>
                    </div>
                    <div>
                      <Button variant="contained" className="back font" onClick={() => window.history.back()}>
                        Back
                      </Button>
                    </div>
                  </div>
                </React.Fragment>
              }
            />
            <Divider className="divider" />
            <CardContent>
              <div className="row">
                <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                  <div>
                    <h3 className="text-primary">Impact</h3>
                    <div className="text-body1 text-grey-8">
                      {threatContainer.impact.split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                          {line.trim()}
                          <br />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <div className="pt-3">
                    {showRemediationButton ? (
                      <div>
                        <Button
                          variant="outlined"
                          className="evidence-button-alert"
                          onClick={() => setShowRemediationButton(false)}
                        >
                          <InfoIcon color="inherit" />
                          Show Remediation
                        </Button>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-primary">Remediation</h3>
                        <div className="text-body1 text-grey-8">
                          {threatContainer.remediation.split('\n').map((line, index) => (
                            <React.Fragment key={index}>
                              {line.trim()}
                              <br />
                            </React.Fragment>
                          ))}
                        </div>
                      </>
                    )}
                    <br/>
                    {showEvidenceButton ? (
                      <div className="pt-3">
                        <Button
                          variant="outlined"
                          className="remediation-button"
                          onClick={() => setShowEvidenceButton(false)}
                        >
                          <WarningIcon color="inherit" />
                          Show Evidence
                        </Button>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-primary">Evidence</h3>
                        <div className="text-body1 text-grey-8" style={{maxHeight:'50vw',overflow:'auto'}}>
                          {parse(decodeHtmlEntities(threatContainer?.evidence || ''))}
                        </div>
                      </>
                    )} 
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                  <Card style={{maxHeight:'100vh',overflow:'auto'}}>
                    <CardContent>
                      <h3 className="text-primary">Finding details</h3>
                      <div className="row">
                        <div className="col-4 text-body1" sx={{ m: 2 }}>
                          <b className="word-wrap">Title</b>:
                        </div>
                        <div className="col-8 text-body1 text-grey-8 word-wrap">
                          {threatContainer?.title}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4 text-body1" sx={{ m: 2 }}>
                          <b className="word-wrap">Severity</b>:
                        </div>
                        <div className={`col-8 text-body1 text-grey-8 word-wrap ${threatContainer?.severity?.toLowerCase()}-alert`}>
                          {threatContainer?.severity}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-4 text-body1" sx={{ m: 2 }}>
                          <b className="word-wrap">Description</b>:
                        </div>
                        <div className="col-8 text-body1 text-grey-8 word-wrap">
                          {threatContainer?.description}
                        </div>
                      </div>
                      {threatContainer &&
                        threatContainer.data &&
                        Object.keys(threatContainer.data)
                          .filter((property) => property !== 'ThreatActorDescription')
                          .map((property, index) => (
                            <React.Fragment key={index}>
                              <div className="row">
                                <div className="col-4 text-body1">
                                  <strong className="word-wrap">{property === 'ThreatActor' ? 'Threat Actor' : formatPropertyName(property)}</strong>:
                                </div>
                                <div
                                  className={`col-8 text-body1 word-wrap ${property === 'domain' ? 'text-secondary' : property === 'cve' || property === 'ThreatActor' ? 'text-primary' : 'text-grey-8'}`}
                                  onClick={() => property === 'ThreatActor' && handleThreatActorClick()}
                                >
                                  {property === 'Additional References' || property==="Affected URLS" ? (
                                    threatContainer.data[property].split('\n').map((line, index) => (
                                      <React.Fragment key={index}>
                                        {property === "Affected URLS" &&(
                                          line.trim()
                                        )}
                                        {property === "Additional References" && (
                                        <a href={line.trim()} target="_blank" rel="noopener noreferrer" style={{color:'blue',cursor:'pointer'}} className="word-wrap">
                                          {line.trim()}
                                        </a>
                                      )}
                                        {index < threatContainer.data[property].split('\n').length - 1 && <br />}
                                      </React.Fragment>
                                    ))
                                  ) : (
                                    typeof threatContainer.data[property] === 'string' ? (
                                      threatContainer.data[property].split('\n').map((line, index, array) => (
                                        <React.Fragment key={index}>
                                          {line.trim()}
                                          {index < array?.length - 1 && <br />}
                                        </React.Fragment>
                                      ))
                                    ) : (
                                      threatContainer.data[property]
                                    )
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          ))}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <Dialog open={threatActor} onClose={() => setThreatActor(false)}>
        <DialogTitle>
          Threat actor description
          <IconButton onClick={() => setThreatActor(false)} className='cross-icon'>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {threatContainer && threatContainer.data &&
              <p className="pre-line">{threatContainer.data.ThreatActorDescription}</p>
            }
            <br />
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SingleAlertPage;
